import MathWrapper from "@/src/components/journal-site/articles/article-body/equation/equation";
import PlateCaption from "@/src/components/journal-site/articles/article-body/plate-caption";
import {
  FONT_TEXT,
  HEADING_SIZE,
} from "@/src/components/common/broadsheet/tokens";
import dynamic from "next/dynamic";
import Image from "next/image";
import ReactHtmlParser from "@/src/utils/html-parser";
const TableContent = dynamic(
  () =>
    import("@/src/components/journal-site/articles/article-body/table/table-content"),
);
import { Box, Link, Typography } from "@mui/material";
import type ArticleBodySectionContent from "@/src/_types/Article/article-body-section-content";
import NextLink from "next/link";
import { getArticleFigureUrl } from "@/src/utils/paths";

const hasMath = (html: string) => /<math\b/i.test(html);

const asDisplayMath = (html: string) =>
  html.replace(/<math\b(?![^>]*\sdisplay=)/gi, '<math display="block"');

const RecursiveContent = ({
  content,
  journalAbbr,
  documentId,
  isDraftEnabled,
  figures,
}: {
  content: ArticleBodySectionContent | ArticleBodySectionContent[] | string;
  journalAbbr: string;
  documentId: number;
  isDraftEnabled: boolean;
  figures?: Figure[] | null | undefined;
}) => {
  if (Array.isArray(content)) {
    return (
      <Box>
        {content.map((item, index) => (
          <RecursiveContent
            key={index}
            content={item}
            journalAbbr={journalAbbr}
            documentId={documentId}
            isDraftEnabled={isDraftEnabled}
            figures={figures}
          />
        ))}
      </Box>
    );
  }
  if (
    typeof content === "object" &&
    content !== null &&
    content.type &&
    content.type !== ""
  ) {
    switch (content.type) {
      case "paragraph": {
        const html = content.content as string;
        const paragraph = ReactHtmlParser(html);
        return (
          <Typography
            component="div"
            sx={{
              mb: 1,
              textIndent: "1.5rem",
            }}
          >
            {hasMath(html) ? <MathWrapper>{paragraph}</MathWrapper> : paragraph}
          </Typography>
        );
      }
      case "table": {
        return (
          <Box component="figure" id={content.id} sx={{ m: 0, mb: 2 }}>
            <PlateCaption
              label={content.label}
              text={content?.caption?.title}
              placement="above"
            />
            <TableContent
              content={content.content as string}
              label={content.label}
            />
            {Array.isArray(content.footnotes) &&
              content.footnotes?.length > 0 && (
                <Box sx={{ pt: 0.375 }}>
                  {content.footnotes.map((footnote) => (
                    <Box
                      key={footnote.id}
                      sx={{
                        fontFamily: FONT_TEXT,
                        fontSize: "0.75rem",
                        lineHeight: 1.45,
                        color: "custom.inkMuted",
                        "& p": { m: 0 },
                      }}
                    >
                      {ReactHtmlParser(footnote.content)}
                    </Box>
                  ))}
                </Box>
              )}
          </Box>
        );
      }
      case "figure": {
        const imageUrl = isDraftEnabled
          ? (() => {
              const figure = figures?.find(
                (f) => f.filename === content.filename,
              );
              return figure?.content || null;
            })()
          : content.url || null;

        return (
          imageUrl &&
          imageUrl !== "" && (
            <Box component="figure" id={content.id} sx={{ m: 0, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  p: 0.25,
                  border: "1px solid",
                  borderColor: "custom.ruleHairline",
                  backgroundColor: "custom.newsprintRaised",
                }}
              >
                <Link
                    component={NextLink}
                    href={
                      isDraftEnabled
                        ? ""
                        : getArticleFigureUrl(
                            journalAbbr,
                            documentId,
                            content.filename,
                            true,
                          )
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: "flex", width: "100%" }}
                  >
                    <Image
                      src={
                        isDraftEnabled
                          ? imageUrl
                          : getArticleFigureUrl(
                              journalAbbr,
                              documentId,
                              content.filename,
                              true,
                            )
                      }
                      title={content?.caption?.title || ""}
                      alt={content?.caption?.p || content?.caption?.title || ""}
                      width={500}
                      height={500}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </Link>
              </Box>
              <Box component="figcaption">
                <PlateCaption
                  label={content?.label}
                  text={content?.caption?.p || content?.caption?.title}
                  placement="below"
                />
              </Box>
            </Box>
          )
        );
      }
      case "equation": {
        return (
          content.content && content.content.length > 0 && (
            <Box component="figure" id={content.id} sx={{ m: 0, mb: 2 }}>
              <Box sx={{ maxWidth: "100%", overflowX: "auto", py: 0.25 }}>
                <MathWrapper>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: asDisplayMath(content.content as string),
                    }}
                  />
                </MathWrapper>
              </Box>
              <Box component="figcaption">
                <PlateCaption
                  label={content.label}
                  text={content?.caption?.title}
                  placement="below"
                />
              </Box>
            </Box>
          )
        );
      }
      case "section": {
        return (
          <Box
            component="article"
            id={content.id}
            sx={{
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              component="h3"
              sx={{
                fontSize: HEADING_SIZE.subsection,
                marginBlockEnd: "0 !important",
              }}
            >
              {ReactHtmlParser(content.title ?? "")}
            </Typography>
            {content.content &&
              typeof content.content !== "string" &&
              content.content.map((child: any, index: number) => (
                <RecursiveContent
                  key={index}
                  content={child}
                  journalAbbr={journalAbbr}
                  documentId={documentId}
                  isDraftEnabled={isDraftEnabled}
                  figures={figures}
                />
              ))}
          </Box>
        );
      }
      case "list": {
        const ListComponent = content.list_type === "order" ? "ol" : "ul";
        return (
          <ListComponent>
            {content.content &&
              typeof content.content !== "string" &&
              content.content.map((child: any, index: number) => (
                <li key={index}>
                  <RecursiveContent
                    content={child}
                    journalAbbr={journalAbbr}
                    documentId={documentId}
                    isDraftEnabled={isDraftEnabled}
                    figures={figures}
                  />
                </li>
              ))}
          </ListComponent>
        );
      }
      default: {
        return <Typography>{JSON.stringify(content)}</Typography>;
      }
    }
  }

  return (
    <Typography component="div">
      {ReactHtmlParser(content as string)}
    </Typography>
  );
};

export default RecursiveContent;
